import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaTrash, FaArrowLeft, FaShoppingBag, FaCreditCard, FaLock, FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaCity, FaFileAlt } from 'react-icons/fa';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const CART_KEY = 'cccigar_cart';

const getCartFromStorage = () => {
  try {
    const cart = localStorage.getItem(CART_KEY);
    return cart ? JSON.parse(cart) : [];
  } catch {
    return [];
  }
};

const saveCartToStorage = (cart) => {
  localStorage.setItem(CART_KEY, JSON.stringify(cart));
};

const Cart = () => {
  const [cartItems, setCartItems] = useState(getCartFromStorage());
  const [showCheckout, setShowCheckout] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    notes: ''
  });
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    setCartItems(getCartFromStorage());
  }, []); // Only on mount

  useEffect(() => {
    saveCartToStorage(cartItems);
  }, [cartItems]);

  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId
          ? { ...item, quantity: Math.min(newQuantity, 10) }
          : item
      )
    );
  };

  const removeItem = (itemId) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== itemId));
  };

  const calculateSubtotal = () => {
    return cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * 0.08; // 8% tax
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const handleCheckout = () => {
    setShowCheckout(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCheckoutForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    
    if (!checkoutForm.name || !checkoutForm.email || !checkoutForm.phone || !checkoutForm.address || !checkoutForm.city) {
      alert(t('common.fillRequiredFields'));
      return;
    }

    setIsSubmitting(true);

    try {
      // Get user token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        alert(t('cart.loginToOrder'));
        navigate('/login');
        return;
      }

      const orderData = {
        items: cartItems.map(item => ({
          product: item.id,
          quantity: item.quantity,
          priceType: item.priceType,
          unitPrice: item.price,
          currency: item.currency
        })),
        totalPrice: calculateTotal(),
        customerInfo: checkoutForm
      };

      const response = await axios.post(API_ENDPOINTS.ORDERS.CREATE, orderData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Clear cart
        localStorage.removeItem(CART_KEY);
        setCartItems([]);
        
        // Show success message
        alert(t('cart.orderSuccess'));
        
        // Redirect to home or orders page
        navigate('/');
      }
    } catch (error) {
      console.error('Order submission error:', error);
      alert(t('cart.orderFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cigar-cream to-cigar-silver flex items-center justify-center">
        <div className="text-center">
          <div className="w-24 h-24 bg-gradient-to-br from-cigar-gold to-cigar-bronze rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <FaShoppingBag className="text-3xl text-cigar-dark" />
          </div>
          <h1 className="text-3xl font-cigar font-bold text-cigar-dark mb-4">{t('cart.emptyCart')}</h1>
          <p className="text-cigar-charcoal mb-8">{t('cart.emptyCartDesc')}</p>
          <Link
            to="/products"
            className="bg-gradient-to-r from-cigar-brown to-cigar-light hover:from-cigar-dark hover:to-cigar-brown text-white font-semibold py-3 px-8 rounded-lg transition-all duration-200 inline-flex items-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            <FaArrowLeft className="mr-2" />
            {t('products.shopNow')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cigar-cream to-cigar-silver py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-cigar font-bold text-cigar-dark mb-2">{t('cart.title')}</h1>
          <p className="text-cigar-charcoal text-base sm:text-lg">{t('cart.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <div key={item.id} className="bg-white rounded-xl shadow-xl p-4 sm:p-6 border border-cigar-silver hover:shadow-2xl transition-all duration-300">
                <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                  {/* Product Image */}
                  <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-cigar-cream to-cigar-silver rounded-lg overflow-hidden flex-shrink-0 shadow-md">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0 w-full sm:w-auto">
                    <h3 className="text-base sm:text-lg font-semibold text-cigar-dark truncate">
                      {item.name}
                    </h3>
                    <p className="text-xs sm:text-sm text-cigar-charcoal mb-2">
                      {item.brand} • {item.category}
                    </p>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                      <div className="space-y-1">
                        <span className="text-lg sm:text-xl font-bold text-cigar-bronze">
                          {item.currency} {item.price}
                        </span>
                        {item.priceType && (
                          <span className="text-xs text-cigar-charcoal bg-cigar-cream px-2 py-1 rounded-full">
                            {item.priceType === 'single' && t('products.single')}
                            {item.priceType === 'box' && `${t('products.box')}(${item.box_size})`}
                            {item.priceType === 'packet' && t('products.packet')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Quantity Controls */}
                  <div className="flex items-center justify-between w-full sm:w-auto sm:space-x-3">
                    <div className="flex items-center border border-cigar-silver rounded-lg shadow-sm">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="px-2 sm:px-3 py-2 text-cigar-charcoal hover:text-cigar-dark hover:bg-cigar-cream disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        disabled={item.quantity <= 1}
                      >
                        -
                      </button>
                      <span className="px-3 sm:px-4 py-2 border-x border-cigar-silver min-w-[2.5rem] sm:min-w-[3rem] text-center text-cigar-dark font-medium text-sm sm:text-base">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="px-2 sm:px-3 py-2 text-cigar-charcoal hover:text-cigar-dark hover:bg-cigar-cream disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        disabled={item.quantity >= 10}
                      >
                        +
                      </button>
                    </div>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors duration-200"
                    >
                      <FaTrash className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Item Total */}
                <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-cigar-silver text-right">
                  <span className="text-base sm:text-lg font-semibold text-cigar-bronze">
                    {t('cart.total')}: {item.currency} {(item.price * item.quantity).toFixed(2)}
                  </span>
                </div>
              </div>
            ))}

            {/* Checkout Form */}
            {showCheckout && (
              <div className="bg-white rounded-xl shadow-xl p-4 sm:p-6 mt-4 sm:mt-6 border border-cigar-silver">
                <h2 className="text-xl sm:text-2xl font-cigar font-bold text-cigar-dark mb-4 sm:mb-6 flex items-center">
                  <FaCreditCard className="mr-2 sm:mr-3 text-cigar-bronze" />
                  {t('cart.checkoutForm')}
                </h2>
                
                <form onSubmit={handleSubmitOrder} className="space-y-3 sm:space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {/* Name */}
                    <div>
                      <label className="block text-sm font-medium text-cigar-dark mb-2">
                        <FaUser className="inline mr-2 text-cigar-bronze" />
                        {t('cart.fullName')} *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={checkoutForm.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-cigar-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-cigar-bronze focus:border-transparent transition-all duration-200"
                        placeholder={t('auth.namePlaceholder')}
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-cigar-dark mb-2">
                        <FaEnvelope className="inline mr-2 text-cigar-bronze" />
                        {t('cart.email')} *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={checkoutForm.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-cigar-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-cigar-bronze focus:border-transparent transition-all duration-200"
                        placeholder={t('auth.emailPlaceholder')}
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-medium text-cigar-dark mb-2">
                        <FaPhone className="inline mr-2 text-cigar-bronze" />
                        {t('cart.phone')} *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={checkoutForm.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-cigar-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-cigar-bronze focus:border-transparent transition-all duration-200"
                        placeholder={t('auth.phonePlaceholder')}
                      />
                    </div>

                    {/* City */}
                    <div>
                      <label className="block text-sm font-medium text-cigar-dark mb-2">
                        <FaCity className="inline mr-2 text-cigar-bronze" />
                        {t('cart.city')} *
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={checkoutForm.city}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-cigar-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-cigar-bronze focus:border-transparent transition-all duration-200"
                        placeholder={t('auth.cityPlaceholder')}
                      />
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <label className="block text-sm font-medium text-cigar-dark mb-2">
                      <FaMapMarkerAlt className="inline mr-2 text-cigar-bronze" />
                      {t('cart.shippingAddress')} *
                    </label>
                    <textarea
                      name="address"
                      value={checkoutForm.address}
                      onChange={handleInputChange}
                      required
                      rows="3"
                      className="w-full px-3 py-2 border border-cigar-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-cigar-bronze focus:border-transparent transition-all duration-200"
                      placeholder={t('auth.addressPlaceholder')}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {/* Postal Code */}
                    <div>
                      <label className="block text-sm font-medium text-cigar-dark mb-2">
                        {t('cart.postalCode')}
                      </label>
                      <input
                        type="text"
                        name="postalCode"
                        value={checkoutForm.postalCode}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-cigar-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-cigar-bronze focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                        placeholder={t('auth.postalCodePlaceholder')}
                      />
                    </div>

                    {/* Notes */}
                    <div>
                      <label className="block text-sm font-medium text-cigar-dark mb-2">
                        <FaFileAlt className="inline mr-2 text-cigar-bronze" />
                        {t('cart.notes')}
                      </label>
                      <textarea
                        name="notes"
                        value={checkoutForm.notes}
                        onChange={handleInputChange}
                        rows="3"
                        className="w-full px-3 py-2 border border-cigar-silver rounded-lg focus:outline-none focus:ring-2 focus:ring-cigar-bronze focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                        placeholder={t('auth.notesPlaceholder')}
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 pt-3 sm:pt-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-gradient-to-r from-cigar-brown to-cigar-light hover:from-cigar-dark hover:to-cigar-brown text-white font-semibold py-3 px-4 sm:px-6 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm sm:text-base"
                    >
                      <FaCreditCard />
                      <span>{isSubmitting ? t('cart.placingOrder') : t('cart.placeOrder')}</span>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setShowCheckout(false)}
                      className="px-4 sm:px-6 py-3 border border-cigar-silver text-cigar-charcoal font-medium rounded-lg hover:bg-cigar-cream transition-all duration-200 text-sm sm:text-base"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-xl p-4 sm:p-6 sticky top-8 border border-cigar-silver">
              <h2 className="text-lg sm:text-xl font-semibold text-cigar-dark mb-3 sm:mb-4 flex items-center">
                <FaShoppingBag className="mr-2 text-cigar-bronze" />
                {t('cart.orderSummary')}
              </h2>
              
              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                <div className="flex justify-between text-sm sm:text-base">
                  <span className="text-cigar-charcoal">{t('cart.subtotal')}</span>
                  <span className="font-medium text-cigar-dark">EGP {calculateSubtotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm sm:text-base">
                  <span className="text-cigar-charcoal">{t('cart.tax')}</span>
                  <span className="font-medium text-cigar-dark">EGP {calculateTax().toFixed(2)}</span>
                </div>
                <div className="border-t border-cigar-silver pt-2 sm:pt-3">
                  <div className="flex justify-between">
                    <span className="text-base sm:text-lg font-semibold text-cigar-dark">{t('cart.total')}</span>
                    <span className="text-xl sm:text-2xl font-bold text-cigar-bronze">EGP {calculateTotal().toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Checkout Button */}
              <button
                onClick={handleCheckout}
                className="w-full bg-gradient-to-r from-cigar-brown to-cigar-light hover:from-cigar-dark hover:to-cigar-brown text-white font-semibold py-3 px-4 sm:px-6 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 mb-3 sm:mb-4 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm sm:text-base"
              >
                <FaCreditCard />
                <span>{t('cart.proceedToCheckout')}</span>
              </button>

              {/* Security Notice */}
              <div className="text-center text-sm text-cigar-charcoal bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg border border-green-200">
                <div className="flex items-center justify-center space-x-1 mb-1">
                  <FaLock className="text-green-600" />
                  <span className="font-medium">{t('cart.secureCheckout')}</span>
                </div>
                <p>{t('cart.paymentProtected')}</p>
              </div>

              {/* Continue Shopping */}
              <div className="mt-6 pt-6 border-t border-cigar-silver">
                <Link
                  to="/products"
                  className="block text-center text-cigar-bronze hover:text-cigar-dark font-medium transition-colors duration-200 hover:underline"
                >
                  {t('cart.continueShopping')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Utility for adding to cart from other pages
export const addToCart = (product, quantity = 1) => {
  const cart = getCartFromStorage();
  const productId = product._id || product.id;
  console.log('Adding to cart:', { product, productId, quantity }); // Debug log
  const existing = cart.find(item => item.id === productId && item.priceType === product.priceType);
  if (existing) {
    existing.quantity = Math.min(existing.quantity + quantity, 10);
  } else {
    cart.push({
      id: productId,
      name: product.product_name || product.name,
      price: product.price,
      image: product.image,
      brand: product.brand,
      category: product.category,
      currency: product.currency,
      priceType: product.priceType,
      box_size: product.box_size,
      quantity
    });
  }
  saveCartToStorage(cart);
};

export default Cart;
